import mixpanel from "mixpanel-browser";
import type { BeforeSendHookPayload, Mixpanel } from "mixpanel-browser";

const URL_PROPERTIES = ["$current_url", "$referrer", "$initial_referrer"];

const redactVerificationUrl = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(redactVerificationUrl);
  if (typeof value !== "string") return value;
  try {
    const url = new URL(value);
    if (!url.searchParams.has("email-uuid")) return value;
    url.searchParams.delete("email-uuid");
    return url.toString();
  } catch {
    return value;
  }
};

const sanitizeUrlProperties = (
  properties: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [
      key,
      URL_PROPERTIES.includes(key) ? redactVerificationUrl(value) : value,
    ])
  );

const registerInitialReferrer = (sdk: Mixpanel, referrer: string): void => {
  if (
    sdk.get_property("$initial_referrer") &&
    sdk.get_property("$initial_referring_domain")
  )
    return;
  sdk.register_once(
    {
      $initial_referrer: referrer || "$direct",
      $initial_referring_domain: referrer ? new URL(referrer).host : "$direct",
    },
    ""
  );
};

export const prepareAnalyticsUrls = (sdk: Mixpanel): void => {
  const referrer = redactVerificationUrl(document.referrer) as string;
  registerInitialReferrer(sdk, referrer);
  for (const key of URL_PROPERTIES) {
    const value = sdk.get_property(key);
    const sanitized = redactVerificationUrl(value);
    if (sanitized !== value) sdk.register({ [key]: sanitized });
  }
  sdk.register(
    {
      $current_url: redactVerificationUrl(window.location.href),
      $referrer: referrer,
    },
    { persistent: false }
  );
};

const sanitizeProfileUrls = (
  payload: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [
      key,
      value && typeof value === "object" && !Array.isArray(value)
        ? sanitizeUrlProperties(value as Record<string, unknown>)
        : value,
    ])
  );

export const analyticsPrivacyHooks = {
  before_identify: (userId: string): string => {
    prepareAnalyticsUrls(mixpanel);
    return userId;
  },
  before_track: (
    name: string,
    properties: Record<string, unknown>
  ): Array<string | Record<string, unknown>> => {
    prepareAnalyticsUrls(mixpanel);
    return [name, properties];
  },
  before_send_events: (
    event: BeforeSendHookPayload
  ): BeforeSendHookPayload => ({
    ...event,
    properties: sanitizeUrlProperties(event.properties),
  }),
  before_send_people: sanitizeProfileUrls,
};
